import { UILanguage, DEFAULT_UI_LANGUAGE, UI_LANGUAGE_STORAGE_KEY, type TranslationKeys } from './types';
import { zhCN } from './locales/zh-CN';
import { enUS } from './locales/en-US';
import { jaJP } from './locales/ja-JP';

/**
 * All available translations
 */
const translations: Record<UILanguage, TranslationKeys> = {
  [UILanguage.ZH_CN]: zhCN,
  [UILanguage.EN_US]: enUS,
  [UILanguage.JA_JP]: jaJP,
  // Add more languages as needed
  // [UILanguage.KO_KR]: enUS, // Fallback to English for now
  // [UILanguage.ES_ES]: enUS, // Fallback to English for now
  // [UILanguage.FR_FR]: enUS, // Fallback to English for now
  // [UILanguage.DE_DE]: enUS, // Fallback to English for now
  // [UILanguage.RU_RU]: enUS, // Fallback to English for now
  // [UILanguage.PT_BR]: enUS, // Fallback to English for now
  // [UILanguage.IT_IT]: enUS, // Fallback to English for now
};

/**
 * Get translation for a specific language
 */
export function getTranslations(language: UILanguage): TranslationKeys {
  return translations[language] || translations[DEFAULT_UI_LANGUAGE];
}

/**
 * Get nested translation value by key path
 * Example: getTranslationValue(translations, 'common.loading') => '加载中...'
 */
export function getTranslationValue(
  translations: TranslationKeys,
  keyPath: string,
  fallback?: string
): string {
  const keys = keyPath.split('.');
  let value: any = translations;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return fallback || keyPath;
    }
  }

  return typeof value === 'string' ? value : fallback || keyPath;
}

/**
 * Get locale string for date formatting based on UI language
 */
export function getDateLocale(language: UILanguage): string {
  switch (language) {
    case UILanguage.ZH_CN:
      return 'zh-CN';
    case UILanguage.EN_US:
      return 'en-US';
    case UILanguage.JA_JP:
      return 'ja-JP';
    default:
      return 'en-US';
  }
}

/**
 * Format date according to current UI language
 */
export function formatDate(dateString: string, language: UILanguage): string {
  const date = new Date(dateString);
  const locale = getDateLocale(language);

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Detect browser language and return supported UI language
 */
export function detectBrowserLanguage(): UILanguage {
  if (typeof window === 'undefined') {
    return UILanguage.EN_US; // Server-side fallback to English
  }

  const browserLang = navigator.language || navigator.languages?.[0];

  if (!browserLang) {
    return UILanguage.EN_US; // No language detected, fallback to English
  }

  // Direct match
  if (Object.values(UILanguage).includes(browserLang as UILanguage)) {
    return browserLang as UILanguage;
  }

  // Language code match (e.g., 'zh' matches 'zh-CN')
  const langCode = browserLang.split('-')[0].toLowerCase();

  switch (langCode) {
    case 'zh':
      return UILanguage.ZH_CN;
    case 'en':
      return UILanguage.EN_US;
    case 'ja':
      return UILanguage.JA_JP;
    // case 'ko':
    //   return UILanguage.KO_KR;
    // case 'es':
    //   return UILanguage.ES_ES;
    // case 'fr':
    //   return UILanguage.FR_FR;
    // case 'de':
    //   return UILanguage.DE_DE;
    // case 'ru':
    //   return UILanguage.RU_RU;
    // case 'pt':
    //   return UILanguage.PT_BR;
    // case 'it':
    //   return UILanguage.IT_IT;
    default:
      return UILanguage.EN_US; // Fallback to English for unsupported languages
  }
}

/**
 * Load UI language from localStorage
 */
export function loadUILanguageFromStorage(): UILanguage {
  if (typeof window === 'undefined') {
    return detectBrowserLanguage(); // Use browser detection even on server side
  }

  try {
    const stored = localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
    if (stored && Object.values(UILanguage).includes(stored as UILanguage)) {
      return stored as UILanguage;
    }
  } catch (error) {
    console.warn('Failed to load UI language from localStorage:', error);
  }

  // Fallback to browser detection
  return detectBrowserLanguage();
}

/**
 * Save UI language to localStorage
 */
export function saveUILanguageToStorage(language: UILanguage): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to save UI language to localStorage:', error);
  }
}

/**
 * Check if a language is supported
 */
export function isUILanguageSupported(language: string): language is UILanguage {
  return Object.values(UILanguage).includes(language as UILanguage);
}

/**
 * Get all available UI languages
 */
export function getAvailableUILanguages(): UILanguage[] {
  return Object.values(UILanguage);
}

// Export types and constants
export * from './types';
export { translations };
